import React, { createContext, useContext, useState, useEffect } from 'react';
import * as meta from '../analytics/meta';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

/**
 * Build a deterministic key that uniquely identifies one cart "line".
 *
 * Two items with the same `item_id` but different `customizations` MUST
 * produce different keys so they don't collapse into a single line with a
 * higher count — the kitchen needs to see them as separate orders.
 *
 * We canonicalise the customizations object before hashing so that the
 * key order of the source dict cannot accidentally change the line key.
 */
const canonicalize = (value) => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((acc, k) => {
        acc[k] = canonicalize(value[k]);
        return acc;
      }, {});
  }
  return value;
};

export const computeLineKey = (item) => {
  const customizationsKey = item.customizations
    ? JSON.stringify(canonicalize(item.customizations))
    : '';
  return `${item.item_id}::${customizationsKey}`;
};

export const CartProvider = ({ children }) => {
  // Hydrate synchronously from localStorage on first render so the cart is
  // never briefly "empty" during full-page navigations (which would otherwise
  // race with the persistence effect and wipe localStorage).
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cart');
      const parsed = saved ? JSON.parse(saved) : [];
      // Back-fill `line_key` for carts persisted before line-key support
      // existed so quantity updates and removals keep working after upgrade.
      return parsed.map((i) => (i.line_key ? i : { ...i, line_key: computeLineKey(i) }));
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item) => {
    const line_key = computeLineKey(item);
    // Fire the Meta Pixel `AddToCart` event once per invocation regardless
    // of whether we merge with an existing line — the customer's *intent*
    // is what Ads Manager needs to optimise on, not our internal
    // deduplication logic.
    meta.addToCart(item);
    setCartItems((prev) => {
      const existing = prev.find((i) => i.line_key === line_key);
      if (existing) {
        // Same item + same customizations → just bump the quantity.
        return prev.map((i) =>
          i.line_key === line_key
            ? { ...i, quantity: i.quantity + (item.quantity || 1) }
            : i,
        );
      }
      // Different customization combo (even for the same menu item) gets its
      // own line so it's visible to the kitchen as a separate order.
      return [...prev, { ...item, line_key, quantity: item.quantity || 1 }];
    });
  };

  const removeFromCart = (lineKey) => {
    setCartItems((prev) => prev.filter((item) => item.line_key !== lineKey));
  };

  const updateQuantity = (lineKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(lineKey);
      return;
    }
    setCartItems((prev) =>
      prev.map((item) =>
        item.line_key === lineKey ? { ...item, quantity } : item,
      ),
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      isCartOpen,
      setIsCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getTotalPrice,
      getTotalItems,
    }}>
      {children}
    </CartContext.Provider>
  );
};
